import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthService, StateUtils } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { filter, map, share, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { UserIdService } from '../../auth';
import { CreateOrUpdateOrderCardConfiguration, OrderCard, OrderCardConfiguration, Pagination, SearchParams } from '../../model';
import { withdrawOn } from '../../util/rxjs';
import { OrderCardsFacade, PrincipalConfigurationService } from '../index';
import { UserActions } from '../store/actions';
import { UserSelectors } from '../store/selectors';
import {
  getOrderCardByCodeState,
  getOrderCardConfigurantionEntityState,
  getOrderCardConfigurantionListState,
  getOrderCardConfigurantionLoadingState,
} from '../store/selectors/order-cards.selectors';
import { StateWithUser } from '../store/user-state';

@Injectable()
export class OrderCardsService implements OrderCardsFacade {
  constructor(
    private store: Store<StateWithUser>,
    private authService: AuthService,
    private userIdService: UserIdService,
    private principalConfigurationService: PrincipalConfigurationService
  ) {}

  getOrderCard(code: string): Observable<OrderCard> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(getOrderCardByCodeState(code))),
      filter((aps) => {
        if (this.needsToLoad(aps)) {
          this.loadOrderCard(code);
          return false;
        }
        return true;
      }),
      map((aps) => {
        return aps.value;
      }),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  private loadOrderCard(code: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadOrderCard(code, userId)),
      () => {}
    );
  }

  searchOrderCards(searchParams: SearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.SearchOrderCards({ userId, searchParams })),
      () => {}
    );
  }

  getOrderCardsSearchResult(searchParams: SearchParams): Observable<OrderCard[] | undefined> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(UserSelectors.OrderCardsSelectors.getSearchResultEntries(searchParams))),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getSearchResultPagination(searchParams: SearchParams): Observable<Pagination | undefined> {
    return this.store.select(UserSelectors.OrderCardsSelectors.getSearchPaginationSelector(searchParams));
  }

  getSearchPageResultsLoading(searchParams: SearchParams): Observable<boolean> {
    return this.store.select(UserSelectors.OrderCardsSelectors.getSearchPageResultsLoading(searchParams));
  }

  loadOrderCardConfigurations(code: string): void {
    this.userIdService
      .takeUserId(true)
      .pipe(
        withLatestFrom(this.store.select(getOrderCardConfigurantionListState(code))),
        filter(([_userId, listState]) => this.needsToLoad(listState)),
        map(([userId, _listState]) => userId)
      )
      .subscribe(
        (userId) => this.store.dispatch(new UserActions.LoadOrderCardConfigurations(code, userId)),
        () => {}
      );
  }

  getOrderCardConfiguration(code: string, productCode: string): Observable<OrderCardConfiguration> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(getOrderCardConfigurantionEntityState(`${code}|${productCode}`))),
      withLatestFrom(this.store.select(getOrderCardConfigurantionListState(code))),
      filter(([entityState, listState]) => {
        if (this.needsToLoad(entityState) && !listState.success && !listState.loading) {
          this.loadOrderCardConfiguration(code, productCode);
          return false;
        }
        return true;
      }),
      map(([entityState, _listState]) => {
        return entityState.value;
      }),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getOrderCardConfigurationLoading(code: string, productCode: string): Observable<boolean> {
    return this.store.select(getOrderCardConfigurantionLoadingState(`${code}|${productCode}`));
  }

  private loadOrderCardConfiguration(code: string, productCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadOrderCardConfiguration(code, userId, productCode)),
      () => {}
    );
  }

  updateOrderCardConfiguration(code: string, productCode: string, params: CreateOrUpdateOrderCardConfiguration): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateOrderCardConfiguration(code, userId, productCode, params)),
      () => {}
    );
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<OrderCardConfiguration | OrderCard>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }

  private hasOrderCard(): Observable<boolean> {
    return this.getOrderCardsSearchResult({ key: 'latest', count: 1, page: 0 }).pipe(
      tap((result) => {
        if (result === undefined) {
          this.searchOrderCards({ key: 'latest', count: 1, page: 0 });
        }
      }),
      map((result) => Boolean(result?.length))
    );
  }

  isOrderCardsUserEnabled(): Observable<boolean> {
    return this.principalConfigurationService
      .isEnabled('enableOrderCardsUser')
      .pipe(switchMap((isEnabled) => (isEnabled ? this.hasOrderCard() : of(false))));
  }
}
